import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Fade,
  Paper,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import { useTranslation } from "react-i18next";
import { Task, WorkflowEventType } from "../../types/tasks";

type FeedbackFieldProps = {
  onSubmit: (feedback: string) => void;
  onClose: () => void;
  initialValue?: string;
  task?: Task;
};

const FeedbackField: React.FC<FeedbackFieldProps> = ({
  onSubmit,
  onClose,
  initialValue = "",
  task,
}) => {
  const [feedback, setFeedback] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  // Determine what text to show: question or description
  let displayText = t("feedbackField.description");

  if (
    task &&
    task.eventType === WorkflowEventType.PausedDueHumanSupportRequired
  ) {
    let question = "";
    if (task.steps?.length) {
      for (let i = task.steps.length - 1; i >= 0; i--) {
        if (
          task.steps[i].humanSupport?.[0]?.humanSupportMessage?.[0]?.question
        ) {
          question =
            task.steps[i].humanSupport?.[0]?.humanSupportMessage?.[0]
              ?.question || "";
          break;
        }
      }

      displayText = question || t("feedbackField.description");
    }
  }

  const handleSubmit = () => {
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit(feedback);
      setFeedback("");
      setIsSubmitting(false);
    }, 300);
  };

  return (
    <Fade in={true} timeout={300}>
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(to right bottom, #fff8e1, #fffde7)",
          borderRadius: "0.5em",
          overflow: "hidden",
          position: "relative",
          border: "0.0625em solid rgba(245, 158, 11, 0.1)",
          boxShadow: "0 0.125em 0.5em rgba(0,0,0,0.05)",
          mb: "1em",
          width: "100%",
          maxWidth: "31.25em",
          mx: "auto",
          px: "1em",
          [theme.breakpoints.down("sm")]: {
            px: "0.75em",
          },
        }}
      >
        {/* header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: "1em",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
            <Box
              sx={{
                width: "2em",
                height: "2em",
                borderRadius: "50%",
                backgroundColor: "rgba(245, 158, 11, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FeedbackOutlinedIcon
                sx={{
                  color: "#f59e0b",
                  fontSize: "1.25em",
                }}
              />
            </Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ color: "#92400e", fontSize: "0.95em" }}
            >
              {t("feedbackField.title")}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#92400e",
              "&:hover": {
                backgroundColor: "rgba(146, 64, 14, 0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            px: "1em",
            pb: "0.75em",
            color: "#b45309",
            textAlign: "left",
          }}
        >
          {displayText}
        </Typography>

        {/* Input field */}
        <Box sx={{ px: "1em", pb: "1em" }}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            placeholder={
              t("feedbackField.placeholder") || "Digite seu feedback aqui..."
            }
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            variant="outlined"
            sx={{
              fontSize: "0.875em",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: "0.375em",
                transition: "all 0.3s ease",
                "&.Mui-focused": {
                  boxShadow: "0 0 0 0.125em rgba(245, 158, 11, 0.2)",
                },
                "& fieldset": {
                  borderColor: isFocused ? "#f59e0b" : "rgba(0, 0, 0, 0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "#f59e0b",
                },
              },
            }}
          />
        </Box>

        {/* Submit button */}
        <Box
          sx={{
            px: "1em",
            pb: "1.5em",
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
          }}
        >
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!feedback.trim() || isSubmitting}
            sx={{
              backgroundColor: "#f59e0b",
              color: "white",
              fontWeight: 600,
              borderRadius: "0.375em",
              px: "1.25em",
              py: "0.5em",
              fontSize: "0.875rem",
              width: {
                xs: "100%",
                sm: "auto",
              },
              "&:hover": {
                backgroundColor: "#d97706",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(245, 158, 11, 0.3)",
                color: "rgba(255, 255, 255, 0.8)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {t("feedbackField.button")}
          </Button>
        </Box>
      </Paper>
    </Fade>
  );
};

export default FeedbackField;
